import { useEffect, useRef, useState } from "react";
import { IREIGraphDataSet } from "../../../interface/graph";
import { ICashflow } from "../../../interface/propertyTracker";
import REIToggablePanel from "../../../components/ui/toggablePanel";
import {
  cashFlowColors,
  formatNumber,
  formatYAxisTick,
  formatYearAsFY,
  getCurrentFinancialYear,
} from "../../../utils/commonUtil";
import ScrollableGraph from "../../../components/shared/ScrollableGraph";
import { REIProgressSpinner } from "../../../components/ui";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../store";
import { useSelector } from "react-redux";

const CashFlow: React.FC<{
  entityId: string;
  showingTotalSummary: boolean;
  trackerData: {
    Actuals: ICashflow[];
    Forecast: ICashflow[];
    isFromMyTracker?: boolean;
  };
  year?: number;
  onYearChangeCallback?: (year: number) => void;
  showPostExpenseData?: boolean;
}> = ({ entityId, showingTotalSummary, trackerData, year, showPostExpenseData, onYearChangeCallback }) => {
  const [currentYearIndex, setCurrentYearIndex] = useState(0);
  const [chartData, setChartData] = useState<IREIGraphDataSet[]>([] as IREIGraphDataSet[]);
  const [trakerIndexData, setTrakerIndexData] = useState<{
    Actuals: ICashflow[];
    Forecast: ICashflow[];
  }>();
  const [selectedTrakerIndexData, setSelectedTrakerIndexData] = useState<{
    Actuals: ICashflow[];
    Forecast: ICashflow[];
  }>();
  const [years, setYears] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(0);
  const [selectedYearForecast, setSelectedYearForecast] = useState<ICashflow>();
  const displayedYears = 5;
  const [loading, setLoading] = useState<boolean>(false);
  const showSeeAllExpensesLabel = useRef<boolean>(trackerData?.isFromMyTracker);
  const currentYear = Number(new Date().getFullYear().toString());
  const isCurrentFinancialYear = selectedYear?.toString()?.slice(2, 4) === getCurrentFinancialYear();
  const navigate = useNavigate();
  const isLoading = useSelector((state: RootState) => state.myTracker.loading);

  useEffect(() => {
    setLoading(true);
    const getTrackerData = async () => {
      setTrakerIndexData((prevState) => {
        setLoading(false);
        return trackerData;
      });
    };
    if (trackerData) {
      getTrackerData();
    }
  }, [trackerData, entityId, showingTotalSummary]);

  useEffect(() => {
    setLoading(true);
    getChartData();
    setLoading(false);
  }, [trakerIndexData, currentYearIndex, displayedYears, selectedYearForecast]);

  useEffect(() => {
    if (year !== undefined && year !== selectedYear && years.length > 0) {
      setSelectedYear((prev) => year);
      setCurrentYearIndex((prev) => years.indexOf(year.toString()));
    }
  }, [year, years]);

  useEffect(() => {
    years && setSelectedYear((prev) => Number(years[currentYearIndex]));
    if (selectedTrakerIndexData && years.length > 0) {
      const combinedData: ICashflow[] = [];
      const actual = selectedTrakerIndexData.Actuals;
      if (actual) {
        actual.forEach((act) => {
          combinedData.push(act);
        });
      }
      selectedTrakerIndexData.Forecast?.forEach((forecast, index) => {
        if (forecast.Year !== actual[index]?.Year) {
          combinedData.push(forecast);
        }
      });
      currentYearIndex <= selectedTrakerIndexData.Actuals?.length - 1
        ? setSelectedYearForecast(selectedTrakerIndexData.Actuals[currentYearIndex])
        : setSelectedYearForecast(combinedData[currentYearIndex]);
    }
  }, [selectedTrakerIndexData, currentYearIndex]);

  const getChartData = () => {
    if (trakerIndexData) {
      const forecastData = trakerIndexData.Forecast;
      const actualData = trakerIndexData.Actuals;

      const actualMap = actualData.reduce((acc, curr) => {
        acc[curr.Year] = curr;
        return acc;
      }, {} as Record<string, ICashflow>);

      const forecastMap = forecastData.reduce((acc, curr) => {
        acc[curr.Year] = curr;
        return acc;
      }, {} as Record<string, ICashflow>);

      const allYears = Array.from(
        new Set([...forecastData.map((f) => f.Year), ...actualData.map((a) => a.Year)])
      ).sort();

      const datasets = [
        {
          label: "Actual Income",
          key: "IN",
          source: actualMap,
          color: cashFlowColors.actualIncome,
        },
        {
          label: "Actual Expense",
          key: "OUT",
          source: actualMap,
          color: cashFlowColors.actualExpense,
        },
        {
          label: "Forecast Income",
          key: "IN",
          source: forecastMap,
          color: cashFlowColors.forecastIncome,
        },
        {
          label: "Forecast Expense",
          key: "OUT",
          source: forecastMap,
          color: cashFlowColors.forecastExpense,
        },
      ];

      const finalChartData = datasets.map(({ label, key, source, color }) => ({
        label,
        data: allYears.map((year) => {
          const value = source[year]?.[key];
          return value != null ? parseFloat(value) : null;
        }),
        backgroundColor: allYears.map(() => color),
      }));

      setChartData(finalChartData);
      setYears(allYears);
      setSelectedTrakerIndexData(trakerIndexData);
    }
  };

  const customOptions = {
    plugins: {
      maintainAspectRatio: false,
      legend: {
        display: false,
        position: "top",
      },
      backgroundColor: "rgb(206, 205, 205)",
    },
    scales: {
      y: {
        ticks: {
          callback: formatYAxisTick,
        },
        grid: {
          display: false,
        },
        beginAtZero: true,
      },
      x: {
        offset: isCurrentFinancialYear,
        ticks: {
          callback: (value: number, index: number) => {
            const actualIndex = currentYearIndex + index;

            if (years.length > 0 && actualIndex < years.length) {
              return formatYearAsFY(years[actualIndex]);
            }
            return value;
          },
        },
        grid: {
          display: false,
        },
      },
    },
  };

  if (loading || isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <REIProgressSpinner />
      </div>
    );
  }

  const CashFlowHeader = (
    <div className="flex flex-row bg-slate-600 border-solid border-gray-200 border-x border-y rounded-t-md ">
      <div className="w-1/2 text-start text-white font-bold text-sm ml-auto pl-3">
        <h3>Cash Flow</h3>
      </div>

      <div className="w-1/2 text-end pl-3 content-center px-4">
        {showSeeAllExpensesLabel.current && (
          <a
            id="mtcf-lnk-seeallexpenses"
            data-testid="mtcf-lnk-seeallexpenses"
            onClick={() => {
              navigate(`/portfolio-tracker`, {
                state: {
                  tab: "MyCashFlowTab",
                },
              });
            }}
            className="no-underline cursor-pointer text-white"
          >
            {"See all expenses & income >>"}
          </a>
        )}
      </div>
    </div>
  );

  const getClassForValue = (value) => {
    return Number(value) < 0 ? "text-red-500" : "";
  };

  const renderGraphHeader = (
    <div className="flex flex-row gap-1 pt-2">
      <div className="flex w-1/4 content-end items-center justify-center">
        <b>
          {` FY-${selectedYear?.toString().slice(2, 4)} `}
          {isCurrentFinancialYear
            ? "Actuals YTD"
            : Number(selectedYear?.toString().slice(2, 4)) < Number(getCurrentFinancialYear())
            ? "Actuals"
            : "Forecast"}
        </b>
      </div>
      <div className="overflow-x-auto w-full">
        <div className="p-2 flex flex-col text-sm content-center bg-gray-100 border-solid border-gray-200 border-x border-y rounded-md  min-w-[500px]">
          <table className="w-full table-auto">
            <thead>
              <tr className="flex justify-between text-center w-full">
                <th className="w-1/4 px-2">Income</th>
                <th className="w-1/4 px-2">Expenditure</th>
                <th className="w-1/4 px-2">Pre-tax cash flow</th>
                {showPostExpenseData && <th className="w-1/4 px-2">After-tax cash flow</th>}
              </tr>
            </thead>
            <tbody>
              <tr className="flex justify-between text-center w-full">
                <td className="w-1/4 px-2 whitespace-nowrap overflow-hidden text-ellipsis">
                  {selectedYearForecast?.IN !== undefined && !isNaN(Number(selectedYearForecast?.IN)) ? (
                    <b className={getClassForValue(selectedYearForecast?.IN)}>
                      {Number(selectedYearForecast?.IN) < 0
                        ? `($${formatNumber(Math.abs(Number(selectedYearForecast?.IN)))})`
                        : `$ ${formatNumber(Number(selectedYearForecast?.IN))}`}
                    </b>
                  ) : (
                    <b>-</b>
                  )}
                </td>
                <td className="w-1/4 px-2 whitespace-nowrap overflow-hidden text-ellipsis">
                  {selectedYearForecast?.OUT !== undefined && !isNaN(Number(selectedYearForecast?.OUT)) ? (
                    <b className={getClassForValue(selectedYearForecast?.OUT)}>
                      {Number(selectedYearForecast?.OUT) < 0
                        ? `($${formatNumber(Math.abs(Number(selectedYearForecast?.OUT)))})`
                        : `$ ${formatNumber(Number(selectedYearForecast?.OUT))}`}
                    </b>
                  ) : (
                    <b>-</b>
                  )}
                </td>
                <td className="w-1/4 px-2 whitespace-nowrap overflow-hidden text-ellipsis">
                  {selectedYearForecast?.PreTaxCashflow !== undefined &&
                  !isNaN(Number(selectedYearForecast?.PreTaxCashflow)) ? (
                    <b>
                      <span className={getClassForValue(selectedYearForecast.PreTaxCashflow)}>
                        {Number(selectedYearForecast.PreTaxCashflow) < 0
                          ? `($${formatNumber(Math.abs(Number(selectedYearForecast.PreTaxCashflow)))})`
                          : `$ ${formatNumber(Number(selectedYearForecast.PreTaxCashflow))}`}
                      </span>
                    </b>
                  ) : (
                    <b>-</b>
                  )}
                </td>
                {showPostExpenseData && (
                  <td className="w-1/4 px-2 whitespace-nowrap overflow-hidden text-ellipsis">
                    {selectedYearForecast?.PostTaxCashflow !== undefined &&
                    !isNaN(Number(selectedYearForecast?.PostTaxCashflow)) ? (
                      <b>
                        <span className={getClassForValue(selectedYearForecast.PostTaxCashflow)}>
                          {Number(selectedYearForecast.PostTaxCashflow) < 0
                            ? `($${formatNumber(Math.abs(Number(selectedYearForecast.PostTaxCashflow)))})`
                            : `$ ${formatNumber(Number(selectedYearForecast.PostTaxCashflow))}`}
                        </span>
                      </b>
                    ) : (
                      <b>-</b>
                    )}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderGraphLegend = (
    <div className="bg-gray-100 border-solid border-gray-200 border-x border-y rounded-b-md p-3 m-3">
      <div className="flex flex-wrap justify-around">
        <div className="flex gap-2 items-center">
          <span className="w-4 h-4 bg-red-700"></span>
          <span className="text-center">Actual income</span>
        </div>
        <div className="flex gap-2 items-center">
          <div className="w-4 h-4 bg-blue-800"></div>
          <span className="text-center">Actual expenses</span>
        </div>
        <div className="flex gap-2 items-center">
          <div className="w-4 h-4 bg-slate-500"></div>
          <span className="text-center">Forecast income</span>
        </div>
        <div className="flex gap-2 items-center">
          <div className="w-4 h-4 bg-green-600"></div>
          <span className="text-center">Forecast expenses</span>
        </div>
      </div>
    </div>
  );

  const onYearChange = (selectedYearIndex: number) => {
    setCurrentYearIndex((prevData) => selectedYearIndex);
    if (onYearChangeCallback) {
      onYearChangeCallback(Number(years.find((_, index) => index === selectedYearIndex)?.slice(2, 4)));
    }
  };

  return (
    <>
      {!trackerData && loading && (
        <div className="flex items-center">
          <REIProgressSpinner></REIProgressSpinner>
        </div>
      )}

      {!loading && (
        <REIToggablePanel
          contentComponent={
            <div className="flex flex-col">
              {renderGraphHeader}
              {!loading && (
                <div>
                  <ScrollableGraph
                    id="mtcf-graph-cashflow"
                    onYearChange={onYearChange}
                    data={chartData}
                    displayedYears={5}
                    graphType="bar"
                    labels={years}
                    customOptions={customOptions}
                    selectedYearIndex={selectedTrakerIndexData?.Actuals.length != 0 ? currentYearIndex : 0}
                  ></ScrollableGraph>
                  {renderGraphLegend}
                </div>
              )}
            </div>
          }
          header=""
          customHeader={CashFlowHeader}
          istoggleable={false}
          defaultCollapse={false}
        ></REIToggablePanel>
      )}
    </>
  );
};

export default CashFlow;
